import {lazy} from 'react';
import {RouteObject} from 'react-router';
import DiagCpoRecallPage from 'packages/shared/pages/diag/cpo/recall';
import {CreatedMaintenanceRecordRequest, CreatePublicServiceConsentRequest} from '@di-shared/api/di/__generated';
import App from '@di-shared/features/App';
import BottomSheetLayout from '@di-shared/features/common/BottomSheetLayout';
import CategoryGuard from '@di-shared/features/guard/CategoryGuard';
import DateGuard from '@di-shared/features/guard/DateGuard';
import HistoryIdGuard from '@di-shared/features/guard/HistoryIdGuard';
import InsuranceServiceIdGuard from '@di-shared/features/guard/InsuranceServiceIdGuard';
import LicensePlateNumberGuard from '@di-shared/features/guard/LicensePlateNumberGuard';
import ReservationNumberGuard from '@di-shared/features/guard/ReservationNumberGuard';
import VinGuard from '@di-shared/features/guard/VinGuard';
import {PaymentExpressPayStatus} from '@di-shared/features/public-service/highway-unpaid/HighwayUnpaidContents';
import CapitalBridgePage from '@di-shared/pages/[vin]/capital';
import CapitalEvent1Page from '@di-shared/pages/[vin]/capital/event1';
import CapitalFullPage from '@di-shared/pages/[vin]/capital/page';
import CapitalShortHomePage from '@di-shared/pages/[vin]/capital/short';
import CapitalTermsPage from '@di-shared/pages/[vin]/capital/terms';
import CapitalTerms3rdDetailPage from '@di-shared/pages/[vin]/capital/terms/3rd';
import CapitalTermsAccountBookDetailPage from '@di-shared/pages/[vin]/capital/terms/account-book';
import CapitalTermsDetailPage from '@di-shared/pages/[vin]/capital/terms/[termsId]';
import HistoryPage from '@di-shared/pages/[vin]/history';
import HistoryDatePage from '@di-shared/pages/[vin]/history/[date]';
import HistoryDownloadPage from '@di-shared/pages/[vin]/history/download';
import FilteredDateHistoryFilterPage from '@di-shared/pages/[vin]/history/filtered/filter';
import FilteredDateHistoryResultPage from '@di-shared/pages/[vin]/history/filtered/result';
import HistorySettingPage from '@di-shared/pages/[vin]/history/setting';
import HistoryManageTagsPage from '@di-shared/pages/[vin]/history/tags';
import HistoryTripDetailPage from '@di-shared/pages/[vin]/history/trip/[tripId]';
import HomePage from '@di-shared/pages/[vin]/home';
import PublicServiceConsentsPage from '@di-shared/pages/[vin]/public-service/consents';
import PublicServiceConsentsDetailPage from '@di-shared/pages/[vin]/public-service/consents/[category]';
import DemeritEducationAboutPage from '@di-shared/pages/[vin]/public-service/demerit-education/about';
import DemeritEducationReservationPage from '@di-shared/pages/[vin]/public-service/demerit-education/reservation';
import DemeritEducationReservationEditPage from '@di-shared/pages/[vin]/public-service/demerit-education/reservation/edit';
import HighwayUnpaidAboutPage from '@di-shared/pages/[vin]/public-service/highway-unpaid/about';
import HighwayUnpaidSearchPage from '@di-shared/pages/[vin]/public-service/highway-unpaid/search';
import HighwayUnpaidEditSearchPage from '@di-shared/pages/[vin]/public-service/highway-unpaid/search/[licensePlateNumber]';
import LicenseRenewalAboutPage from '@di-shared/pages/[vin]/public-service/license-renewal/about';
import VehicleInspectionAboutPage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/about';
import VehicleInspectionAddVehiclePage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/add';
import VehicleInspectionEditVehiclePage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/edit';
import VehicleInspectionNewVehiclePage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/new';
import VehicleInspectionReservationPage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/reservation/[reservationNumber]';
import VehicleInspectionEditReservationPage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/reservation/[reservationNumber]/edit';
import VehicleInspectionPaymentReservationPage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/reservation/[reservationNumber]/payment';
import VehicleInspectionNewReservationPage from '@di-shared/pages/[vin]/public-service/vehicle-inspection/reservation/new';
import SafeDrivingPage from '@di-shared/pages/[vin]/safe-driving';
import SafeDrivingReportPage from '@di-shared/pages/[vin]/safe-driving/report';
import SafeDrivingTermsPage from '@di-shared/pages/[vin]/safe-driving/terms';
import AboutPage from '@di-shared/pages/about';
import AboutDataExplorePage from '@di-shared/pages/about/data-explore';
import BlankBottomSheetPage from '@di-shared/pages/bottom-sheet/blank';
import CapitalEvent1BottomSheetPage from '@di-shared/pages/bottom-sheet/capital/event1';
import CapitalWithdrawBottomSheetPage from '@di-shared/pages/bottom-sheet/capital/withdraw';
import BatteryEmergencyCallBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/battery/emergency-call';
import EvBatteryChargingHabitBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/ev-battery/charging-habit';
import EvBatteryCoachBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/ev-battery/coach';
import EvBatteryPerformaceBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/ev-battery/performance';
import SestExchangeHistoryAutoBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/exchange/history/auto';
import SestExchangeHistoryManualBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/exchange/history/manual';
import DiagExchangeRegisterDatePickerBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/exchange/register/date-picker';
import SestManagementSettingBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/sest/management-setting';
import TireSelectionBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/tire-selection';
import TireWarningBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/tire-warning';
import UpdateOdometerBottomSheetPage from '@di-shared/pages/bottom-sheet/diag/update-odometer';
import {DatePickerPage} from '@di-shared/pages/bottom-sheet/history/download/datepicker';
import HistoryDriverSelectPage from '@di-shared/pages/bottom-sheet/history/driver-select';
import HistoryFilterSelectPage from '@di-shared/pages/bottom-sheet/history/filter-select';
import SelectMonthPage from '@di-shared/pages/bottom-sheet/history/select-month';
import HistoryTagSelectPage from '@di-shared/pages/bottom-sheet/history/tag-select';
import HistoryTagFormBottomSheetPage from '@di-shared/pages/bottom-sheet/history/tags/form';
import IntroduceBottomSheetPage from '@di-shared/pages/bottom-sheet/introduce';
import MileageSignupEligibilityBottomSheetPage from '@di-shared/pages/bottom-sheet/mileage/MileageSignupEligibilityBottomSheetPage';
import MileageSignupEligibilityImsiBottomSheetPage from '@di-shared/pages/bottom-sheet/mileage/MileageSignupEligibilityImsiBottomSheetPage';
import DemeritEducationFailSystemErrorBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/demerit-education/fail-customer-info';
import DemeritEducationFailCustomerInfoBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/demerit-education/fail-customer-info';
import DemeritEducationReservationFailBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/demerit-education/reservation-cancel';
import DemeritEducationReservationSuccessBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/demerit-education/reservation-success';
import HighwayUnpaidEditConfirmBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/highway-unpaid/edit/confirm';
import LicenseRenewalFailCustomerInfoBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/license-renewal/fail-customer-info';
import LicenseRenewalFailOver75YearsBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/license-renewal/fail-over-75years';
import LicenseRenewalFailPoliceCaseBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/license-renewal/fail-police-case';
import LicenseRenewalFailSystemBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/license-renewal/fail-system-error';
import AuthenticationCountExceededBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/vehicle-inspection/authentication-count-exceeded';
import GeneralFailureBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/vehicle-inspection/general-failure';
import OwnerAuthenticationFailureBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/vehicle-inspection/owner-authentication-failure';
import VehicleInspectionReservationConfirmBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/vehicle-inspection/reservation-confirm';
import PublicServiceWithdrawBottomSheetPage from '@di-shared/pages/bottom-sheet/public-service/withdraw';
import SafeDrivingAgreeBottomSheetPage from '@di-shared/pages/bottom-sheet/safe-driving/agree';
import SafeDrivingGuideBottomSheetPage from '@di-shared/pages/bottom-sheet/safe-driving/guide';
import SafeDrivingNavigationUpdateBottomSheetPage from '@di-shared/pages/bottom-sheet/safe-driving/navigation-update';
import SafeDrivingWithdrawBottomSheetPage from '@di-shared/pages/bottom-sheet/safe-driving/withdraw';
import UbiAgreeBottomSheetPage from '@di-shared/pages/bottom-sheet/ubi/agree';
import UbiNoInsurancePage from '@di-shared/pages/bottom-sheet/ubi/no-insurance';
import UbiWithdrawBottomPage from '@di-shared/pages/bottom-sheet/ubi/withdraw';
import VehicleDetailBottomSheetPage from '@di-shared/pages/bottom-sheet/vehicle/delete';
import DiagPage from '@di-shared/pages/diag';
import DiagBatteryPage from '@di-shared/pages/diag/battery';
import DiagBatteryDischargePage from '@di-shared/pages/diag/battery/discharge';
import DiagBatteryLifespanPage from '@di-shared/pages/diag/battery/lifespan';
import DiagBrakePage from '@di-shared/pages/diag/brake';
import DiagCpoPage from '@di-shared/pages/diag/cpo';
import DiagExchangeHistoryPage from '@di-shared/pages/diag/exchange/history';
import DiagExchangeHistoryDetailEditPage from '@di-shared/pages/diag/exchange/history/[historyId]/edit';
import SestExchangeRegisterPage from '@di-shared/pages/diag/exchange/register';
import SestExchangeSelectPage from '@di-shared/pages/diag/exchange/select';
import SestCategoryDetailPage from '@di-shared/pages/diag/sest/category/[sestCategory]';
import DiagTirePage from '@di-shared/pages/diag/tire';
import ErrorPage from '@di-shared/pages/error';
import FaqPage from '@di-shared/pages/faq';
import CpoGuidePage from '@di-shared/pages/guide/cpo';
import MileageGuidePage from '@di-shared/pages/guide/mileage';
import SafeDrivingGuidePage from '@di-shared/pages/guide/safe-driving';
import MaintenancePage from '@di-shared/pages/maintenance';
import MileagePage from '@di-shared/pages/mileage';
import MileageAgreementPage from '@di-shared/pages/mileage/agreements';
import MileageAgreementDetailPage from '@di-shared/pages/mileage/agreements/[termsId]';
import MileageBalancePage from '@di-shared/pages/mileage/balance';
import EcoMileagePage from '@di-shared/pages/mileage/ecomileage/EcoMileagePage';
import MileageFirstRegisterPage from '@di-shared/pages/mileage/first-register';
import MileageIntroPage from '@di-shared/pages/mileage/intro';
import MileageMidRegisterPage from '@di-shared/pages/mileage/mid-register';
import MileageParticipationGuidePage from '@di-shared/pages/mileage/participation-guide';
import MileageRegisterPage from '@di-shared/pages/mileage/register';
import MileageSafeDrivingPage from '@di-shared/pages/mileage/safe-driving';
import PaymentExpressPayCallbackPage from '@di-shared/pages/payment/express-pay/callback';
import PaymentHmgPayCallbackPage from '@di-shared/pages/payment/hmgpay/callback';
import UbiPage from '@di-shared/pages/ubi';
import UbiDetailPage from '@di-shared/pages/ubi/[serviceId]';

const PageListPage = lazy(() => import('@di-shared/pages/pageList'));

export const ROUTE_PATHS = {
  PAGE_NOT_FOUND: {
    id: 'PAGE_NOT_FOUND',
    getFullPath: () => '/',
  },
  PAGE_LIST: {
    id: 'PAGE_LIST',
    getFullPath: () => '/page-list',
  },
  // 01. HOME
  HOME: {
    id: 'HOME',
    getFullPath: (vin: string) => `/${vin}/home`,
  },
  HOME_DRIVING_TAB: {
    id: 'HOME_DRIVING_TAB',
    getFullPath: (vin: string) => `/${vin}/home?tab=driving`,
  },
  HOME_VEHICLE_TAB: {
    id: 'HOME_VEHICLE_TAB',
    getFullPath: (vin: string) => `/${vin}/home?tab=vehicle`,
  },
  HOME_BENEFIT_TAB: {
    id: 'HOME_BENEFIT_TAB',
    getFullPath: (vin: string) => `/${vin}/home?tab=benefit`,
  },
  HOME_FINANCE_TAB: {
    id: 'HOME_FINANCE_TAB',
    getFullPath: (vin: string) => `/${vin}/home?tab=finance`,
  },
  BOTTOM_SHEET_INTRODUCE: {
    id: 'BOTTOM_SHEET_INTRODUCE',
    getFullPath: () => `/bottom-sheet/introduce`,
  },
  BOTTOM_SHEET_VEHICLE_DELETE: {
    id: 'BOTTOM_SHEET_VEHICLE_DELETE',
    getFullPath: () => `/bottom-sheet/vehicle/delete`,
  },
  // 02. 안전운전점수
  SAFE_DRIVING: {
    id: 'SAFE_DRIVING',
    getFullPath: (vin: string) => `/${vin}/safe-driving`,
  },
  SAFE_DRIVING_GUIDE: {
    id: 'SAFE_DRIVING_GUIDE',
    getFullPath: () => `/guide/safe-driving`,
  },
  SAFE_DRIVING_REPORT: {
    id: 'SAFE_DRIVING_REPORT',
    getFullPath: (vin: string) => `/${vin}/safe-driving/report`,
  },
  SAFE_DRIVING_TERMS: {
    id: 'SAFE_DRIVING_TERMS',
    getFullPath: (vin: string) => `/${vin}/safe-driving/terms`,
  },
  BOTTOM_SHEET_SAFE_DRIVING_GUIDE: {
    id: 'BOTTOM_SHEET_SAFE_DRIVING_GUIDE',
    getFullPath: () => `/bottom-sheet/safe-driving/guide`,
  },
  BOTTOM_SHEET_SAFE_DRIVING_AGREE: {
    id: 'BOTTOM_SHEET_SAFE_DRIVING_AGREE',
    getFullPath: () => `/bottom-sheet/safe-driving/agree`,
  },
  BOTTOM_SHEET_SAFE_DRIVING_WITHDRAW: {
    id: 'BOTTOM_SHEET_SAFE_DRIVING_WITHDRAW',
    getFullPath: () => `/bottom-sheet/safe-driving/withdraw`,
  },
  BOTTOM_SHEET_SAFE_DRIVING_NAVIGATION_UPDATE: {
    id: 'BOTTOM_SHEET_SAFE_DRIVING_NAVIGATION_UPDATE',
    getFullPath: () => `/bottom-sheet/safe-driving/navigation-update`,
  },
  BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY: {
    id: 'BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY',
    getFullPath: () => `/bottom-sheet/mileage/signup-eligibility`,
  },
  /**
   * [2026-02-09:Jeyoung Lee] FIXME: 서울시 임시 페이지
   * @link https://hmg.atlassian.net/browse/DRIVINGINS-2884
   */
  BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY_IMSI: {
    id: 'BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY_IMSI',
    getFullPath: () => `/bottom-sheet/mileage/signup-eligibility-imsi`,
  },
  // 03. 주행기록
  HISTORY: {
    id: 'HISTORY',
    getFullPath: (vin: string) => `/${vin}/history`,
  },
  HISTORY_DRIVING_INFO_TAB: {
    id: 'HISTORY_DRIVING_INFO_TAB',
    getFullPath: (vin: string) => `/${vin}/history?tab=driving-info`,
  },
  HISTORY_EV_ENERGY_USAGE_TAB: {
    id: 'HISTORY_EV_ENERGY_USAGE_TAB',
    getFullPath: (vin: string) => `/${vin}/history?tab=ev-energy-usage`,
  },
  HISTORY_$DATE: {
    id: 'HISTORY_$DATE',
    getFullPath: (vin: string, date: string) => `/${vin}/history/${date}`,
  },
  HISTORY_FILTERED_FILTER: {
    id: 'HISTORY_FILTERED_FILTER',
    getFullPath: (vin: string) => `/${vin}/history/filtered/filter`,
  },
  HISTORY_FILTERED_RESULT: {
    id: 'HISTORY_FILTERED_RESULT',
    getFullPath: (vin: string) => `/${vin}/history/filtered/result`,
  },
  HISTORY_$TRIP_ID: {
    id: 'HISTORY_$TRIP_ID',
    getFullPath: (vin: string, tripId: string) => `/${vin}/history/trip/${tripId}`,
  },
  HISTORY_SETTING: {
    id: 'HISTORY_SETTING',
    getFullPath: (vin: string) => `/${vin}/history/setting`,
  },
  HISTORY_DOWNLOAD: {
    id: 'HISTORY_DOWNLOAD',
    getFullPath: (vin: string) => `/${vin}/history/download`,
  },
  HISTORY_TAGS: {
    id: 'HISTORY_TAGS',
    getFullPath: (vin: string) => `/${vin}/history/tags`,
  },
  BOTTOM_SHEET_HISTORY_SELECT_MONTH: {
    id: 'BOTTOM_SHEET_HISTORY_SELECT_MONTH',
    getFullPath: () => '/bottom-sheet/history/select-month',
  },
  BOTTOM_SHEET_HISTORY_DELETE_MONTH: {
    id: 'BOTTOM_SHEET_HISTORY_DELETE_MONTH',
    getFullPath: () => '/bottom-sheet/history/delete-month',
  },
  BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER: {
    id: 'BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER',
    getFullPath: () => '/bottom-sheet/history/download/datepicker',
  },
  BOTTOM_SHEET_HISTORY_FILTER_SELECT: {
    id: 'BOTTOM_SHEET_HISTORY_FILTER_SELECT',
    getFullPath: () => '/bottom-sheet/history/filter-select',
  },
  BOTTOM_SHEET_HISTORY_DRIVER_SELECT: {
    id: 'BOTTOM_SHEET_HISTORY_DRIVER_SELECT',
    getFullPath: () => '/bottom-sheet/history/driver-select',
  },
  BOTTOM_SHEET_HISTORY_TAG_SELECT: {
    id: 'BOTTOM_SHEET_HISTORY_TAG_SELECT',
    getFullPath: () => '/bottom-sheet/history/tag-select',
  },
  BOTTOM_SHEET_HISTORY_TAGS_FORM: {
    id: 'BOTTOM_SHEET_HISTORY_TAGS_FORM',
    getFullPath: () => '/bottom-sheet/history/tags/form',
  },
  // 04. 차량관리
  DIAG: {
    id: 'DIAG',
    getFullPath: (vin: string) => `/${vin}/diag`,
  },
  DIAG_TIRE: {
    id: 'DIAG_TIRE',
    getFullPath: (vin: string) => `/${vin}/diag/tire`,
  },
  DIAG_BATTERY: {
    id: 'DIAG_BATTERY',
    getFullPath: (vin: string) => `/${vin}/diag/battery`,
  },
  DIAG_BATTERY_DISCHARGE: {
    id: 'DIAG_BATTERY_DISCHARGE',
    getFullPath: (vin: string) => `/${vin}/diag/battery/discharge`,
  },
  DIAG_BATTERY_LIFESPAN: {
    id: 'DIAG_BATTERY_LIFESPAN',
    getFullPath: (vin: string) => `/${vin}/diag/battery/lifespan`,
  },
  DIAG_BRAKE: {
    id: 'DIAG_BRAKE',
    getFullPath: (vin: string) => `/${vin}/diag/brake`,
  },
  DIAG_SEST_$CATEGORY: {
    id: 'DIAG_SEST_$CATEGORY',
    getFullPath: (vin: string, sestCategory: string) => `/${vin}/diag/sest/category/${sestCategory}`,
  },
  DIAG_EXCHANGE_SELECT: {
    id: 'DIAG_EXCHANGE_SELECT',
    getFullPath: (vin: string, sestCodes?: CreatedMaintenanceRecordRequest.sestCode[]) =>
      `/${vin}/diag/exchange/select${sestCodes ? '?' + sestCodes.map((sestCode) => `sestCode=${sestCode}`).join('&') : ''}`,
  },
  DIAG_EXCHANGE_REGISTER: {
    id: 'DIAG_EXCHANGE_REGISTER',
    getFullPath: (vin: string, sestCodes?: CreatedMaintenanceRecordRequest.sestCode[]) =>
      `/${vin}/diag/exchange/register${sestCodes ? '?' + sestCodes.map((sestCode) => `sestCode=${sestCode}`).join('&') : ''}`,
  },
  DIAG_EXCHANGE_HISTORY: {
    id: 'DIAG_EXCHANGE_HISTORY',
    getFullPath: (vin: string) => `/${vin}/diag/exchange/history`,
  },
  DIAG_EXCHANGE_HISTORY_DETAIL_EDIT: {
    id: 'DIAG_EXCHANGE_HISTORY_DETAIL_EDIT',
    getFullPath: (vin: string, historyId: string) => `/${vin}/diag/exchange/history/${historyId}/edit`,
  },
  BOTTOM_SHEET_DIAG_UPDATE_ODOMETER: {
    id: 'BOTTOM_SHEET_DIAG_UPDATE_ODOMETER',
    getFullPath: () => '/bottom-sheet/diag/update-odometer',
  },
  BOTTOM_SHEET_DIAG_TIRE_SELECTION: {
    id: 'BOTTOM_SHEET_DIAG_TIRE_SELECTION',
    getFullPath: (vin: string) => `/bottom-sheet/diag/${vin}/tire-selection`,
  },
  BOTTOM_SHEET_DIAG_TIRE_WARNING: {
    id: 'BOTTOM_SHEET_DIAG_TIRE_WARNING',
    getFullPath: () => `/bottom-sheet/diag/tire-warning`,
  },
  BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING: {
    id: 'BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING',
    getFullPath: (sestCode: string) => `/bottom-sheet/diag/sest/management-setting/${sestCode}`,
  },
  BOTTOM_SHEET_DIAG_BATTERY_EMERGENCY_CALL: {
    id: 'BOTTOM_SHEET_DIAG_BATTERY_EMERGENCY_CALL',
    getFullPath: () => `/bottom-sheet/diag/ev-battery/emergency-call`,
  },
  BOTTOM_SHEET_DIAG_BATTERY_CHARGING_HABIT: {
    id: 'BOTTOM_SHEET_DIAG_BATTERY_CHARGING_HABIT',
    getFullPath: () => `/bottom-sheet/diag/ev-battery/charging-habit`,
  },
  BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE: {
    id: 'BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE',
    getFullPath: () => `/bottom-sheet/diag/ev-battery/performance`,
  },
  BOTTOM_SHEET_DIAG_BATTERY_COACH: {
    id: 'BOTTOM_SHEET_DIAG_BATTERY_COACH',
    getFullPath: () => `/bottom-sheet/diag/ev-battery/coach`,
  },
  BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_AUTO: {
    id: 'BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_AUTO',
    getFullPath: () => `/bottom-sheet/diag/exchange/history/auto`,
  },
  BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_MANUAL: {
    id: 'BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_MANUAL',
    getFullPath: () => `/bottom-sheet/diag/exchange/history/manual`,
  },
  BOTTOM_SHEET_DIAG_EXCHANGE_REGISTER_DATE_PICKER: {
    id: 'BOTTOM_SHEET_DIAG_EXCHANGE_REGISTER_DATE_PICKER',
    getFullPath: () => `/bottom-sheet/diag/exchange/register/date-picker`,
  },

  BOTTOM_SHEET_CAPITAL_WITHDRAW: {
    id: 'BOTTOM_SHEET_CAPITAL_WITHDRAW',
    getFullPath: () => `/bottom-sheet/capital/withdraw`,
  },
  BOTTOM_SHEET_PUBLIC_SERVCIE_WITHDRAW: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVCIE_WITHDRAW',
    getFullPath: () => `/bottom-sheet/public-service/withdraw`,
  },
  // 05. UBI
  UBI: {
    id: 'UBI',
    getFullPath: (vin: string) => `/${vin}/ubi`,
  },
  UBI_DETAIL_$SERVICE_ID: {
    id: 'UBI_DETAIL_$SERVICE_ID',
    getFullPath: ({vin, serviceId}: {vin: string; serviceId: string}) => `/${vin}/ubi/${serviceId}`,
  },
  BOTTOM_SHEET_UBI_NO_INSURANCE: {
    id: 'BOTTOM_SHEET_UBI_NO_INSURANCE',
    getFullPath: () => `/bottom-sheet/ubi/no-insurance`,
  },
  BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_AGREE: {
    id: 'BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_AGREE',
    getFullPath: () => `/bottom-sheet/ubi/agree`,
  },
  BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_WITHDRAW: {
    id: 'BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_WITHDRAW',
    getFullPath: () => `/bottom-sheet/ubi/withdraw`,
  },
  // 06. 차량인증 등급
  CPO: {
    id: 'CPO',
    getFullPath: (vin: string) => `/${vin}/diag/cpo`,
  },
  CPO_GUIDE: {
    id: 'CPO_GUIDE',
    getFullPath: () => `/guide/cpo`,
  },
  CPO_RECALL: {
    id: 'CPO_RECALL',
    getFullPath: (vin: string) => `/${vin}/diag/cpo/recall`,
  },
  // 07. 서울 마일리지 연계
  MILEAGE: {
    id: 'MILEAGE',
    getFullPath: (vin: string) => `/${vin}/mileage`,
  },
  MILEAGE_ECO: {
    id: 'MILEAGE_ECO',
    getFullPath: (vin: string) => `/${vin}/mileage/eco`,
  },
  MILEAGE_SAFE_DRIVING: {
    id: 'MILEAGE_DRIVING',
    getFullPath: (vin: string) => `/${vin}/mileage/safe-driving`,
  },
  MILEAGE_BALANCE: {
    id: 'MILEAGE_BALANCE',
    getFullPath: (vin: string) => `/${vin}/mileage/balance`,
  },
  MILEAGE_INTRO: {
    id: 'MILEAGE_INTRO',
    getFullPath: (vin: string) => `/${vin}/mileage/intro`,
  },
  MILEAGE_PARTICIPATION_GUIDE: {
    id: 'MILEAGE_PARTICIPATION_GUIDE',
    getFullPath: (vin: string) => `/${vin}/mileage/participation-guide`,
  },
  GUIDE_MILEAGE: {
    id: 'GUIDE_MILEAGE',
    getFullPath: () => `/guide/mileage`,
  },
  MILEAGE_REGISTER: {
    id: 'MILEAGE_REGISTER',
    getFullPath: (vin: string) => `/${vin}/mileage/register`,
  },
  MILEAGE_FIRST_REGISTER: {
    id: 'MILEAGE_FIRST_REGISTER',
    getFullPath: (vin: string) => `/${vin}/mileage/first-register`,
  },
  MILEAGE_MID_REGISTER: {
    id: 'MILEAGE_MID_REGISTER',
    getFullPath: (vin: string) => `/${vin}/mileage/mid-register`,
  },
  MILEAGE_AGREEMENT: {
    id: 'MILEAGE_AGREEMENT',
    getFullPath: (vin: string) => `/${vin}/mileage/agreement`,
  },
  MILEAGE_AGREEMENT_DETAIL: {
    id: 'MILEAGE_AGREEMENT_DETAIL',
    getFullPath: (vin: string, termsId: string) => `/${vin}/mileage/agreement/${termsId}`,
  },
  // 08. 현대캐피탈
  CAPITAL_BRIDGE: {
    id: 'CAPITAL_BRIDGE',
    getFullPath: (vin: string) => `/${vin}/capital`,
  },
  CAPITAL_SHORT: {
    id: 'CAPITAL_SHORT',
    getFullPath: (vin: string) => `/${vin}/capital/short`,
  },
  CAPITAL_TERMS: {
    id: 'CAPITAL_TERMS',
    getFullPath: (vin: string) => `/${vin}/capital/terms`,
  },
  CAPITAL_TERMS_DETAIL: {
    id: 'CAPITAL_TERMS_DETAIL',
    getFullPath: (vin: string, termsId: string) => `/${vin}/capital/terms/${termsId}`,
  },
  CAPITAL_TERMS_3RD_DETAIL: {
    id: 'CAPITAL_TERMS_3RD_DETAIL',
    getFullPath: (vin: string) => `/${vin}/capital/terms/3rd`,
  },
  CAPITAL_TERMS_ACCOUNT_BOOK_DETAIL: {
    id: 'CAPITAL_TERMS_ACCOUNT_BOOK_DETAIL',
    getFullPath: (vin: string) => `/${vin}/capital/terms/account-book`,
  },
  CAPITAL_FULL_PAGE: {
    id: 'CAPITAL_FULL_PAGE',
    getFullPath: (vin: string, to: string) => `/${vin}/capital/page?to=${encodeURIComponent(to)}`,
  },
  // [2025-07-24:cy.seo] FIXME: 캐피탈 이벤트 (25.09~) 종료 후 연관 코드 제거할것 EVENT2
  CAPITAL_EVENT_1: {
    id: 'CAPITAL_EVENT_1',
    getFullPath: (vin: string) => `/${vin}/capital/event1`,
  },
  // 09. 공공서비스
  PUBLIC_SERVICE_CONSENTS: {
    id: 'PUBLIC_SERVICE_CONSENTS',
    getFullPath: (vin: string) => `/${vin}/public-service/consents`,
  },
  PUBLIC_SERVICE_CONSENTS_$CATEGORY: {
    id: 'PUBLIC_SERVICE_CONSENTS_$CATEGORY',
    getFullPath: ({vin, category, nextUrl}: {vin: string; category: CreatePublicServiceConsentRequest.category; nextUrl: string}) =>
      `/${vin}/public-service/consents/${category}?nextUrl=${encodeURIComponent(nextUrl)}`,
  },
  PUBLIC_SERVICE_HIGHWAY_UNPAID_ABOUT: {
    id: 'PUBLIC_SERVICE_HIGHWAY_UNPAID_ABOUT',
    getFullPath: (vin: string) => `/${vin}/public-service/highway-unpaid/about`,
  },
  PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH: {
    id: 'PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH',
    getFullPath: (vin: string) => `/${vin}/public-service/highway-unpaid/search`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_HIGHWAY_UNPAID_EDIT_CONFIRM: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_HIGHWAY_UNPAID_EDIT_CONFIRM',
    getFullPath: () => `/bottom-sheet/public-service/highway-unpaid/edit/confirm`,
  },
  PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH_$LICENSE_PLATE_NUMBER: {
    id: 'PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH_$LICENSE_PLATE_NUMBER',
    getFullPath: (vin: string, licensePlateNumber: string) => `/${vin}/public-service/highway-unpaid/search/${licensePlateNumber}`,
  },
  PUBLIC_SERVICE_LICENSE_RENEWAL_ABOUT: {
    id: 'PUBLIC_SERVICE_ABOUT_LICENSE_RENEWAL',
    getFullPath: (vin: string) => `/${vin}/public-service/license-renewal/about`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_CUSTOMER_INFO: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_CUSTOMER_INFO',
    getFullPath: () => `/bottom-sheet/public-service/license-renewal/fail-customer-info`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_SYSTEM_ERROR: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_SYSTEM_ERROR',
    getFullPath: () => `/bottom-sheet/public-service/license-renewal/fail-system-error`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_POLICE_CASE: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_POLICE_CASE',
    getFullPath: () => `/bottom-sheet/public-service/license-renewal/fail-police-case`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_OVER_75YEARS: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_OVER_75YEARS',
    getFullPath: () => `/bottom-sheet/public-service/license-renewal/fail-over-75years`,
  },
  PUBLIC_SERVICE_DEMERIT_EDUCATION_ABOUT: {
    id: 'PUBLIC_SERVICE_DEMERIT_EDUCATION_ABOUT',
    getFullPath: (vin: string) => `/${vin}/public-service/demerit-education/about`,
  },
  PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION: {
    id: 'PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION',
    getFullPath: (vin: string) => `/${vin}/public-service/demerit-education/reservation`,
  },
  PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_EDIT: {
    id: 'PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_EDIT',
    getFullPath: (vin: string) => `/${vin}/public-service/demerit-education/reservation/edit`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_FAIL: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_FAIL',
    getFullPath: () => `/bottom-sheet/public-service/demerit-education/reservation-fail`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_SUCCESS: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_SUCCESS',
    getFullPath: () => `/bottom-sheet/public-service/demerit-education/reservation-success`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_SYSYEM_ERROR: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_SYSYEM_ERROR',
    getFullPath: () => `/bottom-sheet/public-service/demerit-education/fail-system-error`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_CUSTOMER_INFO: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_CUSTOMER_INFO',
    getFullPath: () => `/bottom-sheet/public-service/demerit-education/fail-customer-info`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_NEW: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_NEW',
    getFullPath: (vin: string) => `/${vin}/public-service/vehicle-inspection/new`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_ADD: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_ADD',
    getFullPath: (vin: string) => `/${vin}/public-service/vehicle-inspection/add`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_EDIT: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_EDIT',
    getFullPath: (vin: string, index: number) => `/${vin}/public-service/vehicle-inspection/edit${index ? `?index=${index}` : ''}`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_ABOUT: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_ABOUT',
    getFullPath: (vin: string) => `/${vin}/public-service/vehicle-inspection/about`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_NEW: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_NEW',
    getFullPath: (vin: string, selectedIndex: number) => `/${vin}/public-service/vehicle-inspection/reservation/new?index=${selectedIndex}`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER',
    getFullPath: (vin: string, reservationNumber: string) => `/${vin}/public-service/vehicle-inspection/reservation/${reservationNumber}`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_EDIT: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_EDIT',
    getFullPath: (vin: string, reservationNumber: string) =>
      `/${vin}/public-service/vehicle-inspection/reservation/${reservationNumber}/edit`,
  },
  PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_PAYMENT: {
    id: 'PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_PAYMENT',
    getFullPath: (vin: string, reservationNumber: string) =>
      `/${vin}/public-service/vehicle-inspection/reservation/${reservationNumber}/payment`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_OWNER_AUTHENTICATION_FAILURE: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_OWNER_AUTHENTICATION_FAILURE',
    getFullPath: () => `/bottom-sheet/public-service/vehicle-inspection/owner-authentication-failure`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_GENERAL_FAILURE: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_GENERAL_FAILURE',
    getFullPath: () => `/bottom-sheet/public-service/vehicle-inspection/general-failure`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_AUTHENTICATION_COUNT_EXCEEDED: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_AUTHENTICATION_COUNT_EXCEEDED',
    getFullPath: () => `/bottom-sheet/public-service/vehicle-inspection/authentication-count-exceeded`,
  },
  BOTTOM_SHEET_PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_CONFIRM: {
    id: 'BOTTOM_SHEET_PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_CONFIRM',
    getFullPath: () => `/bottom-sheet/public-service/vehicle-inspection/reservation-confirm`,
  },

  // [2025-07-24:cy.seo] FIXME: 캐피탈 이벤트 (25.11.5~25.12.2) 종료 후 연관 코드 제거할것 EVENT2
  BOTTOM_SHEET_CAPITAL_EVENT_1_INTRO: {
    id: 'BOTTOM_SHEET_CAPITAL_EVENT_1_INTRO',
    getFullPath: () => `/bottom-sheet/capital/event1`,
  },
  HMG_PAY_PAYMENT_CALLBACK: {
    id: 'HMG_PAY_PAYMENT_CALLBACK',
    getFullPath: () => '/payment/hmgpay/callback',
  },
  EXPRESS_PAY_PAYMENT_CALLBACK: {
    id: 'EXPRESS_PAY_PAYMENT_CALLBACK',
    getFullPath: ({status, paymentId}: {status: PaymentExpressPayStatus; paymentId: string}) =>
      `/payment/express-pay/callback?status=${status}&paymentId=${paymentId}`,
  },
  ABOUT: {
    id: 'ABOUT',
    getFullPath: () => '/about',
  },
  ABOUT_DATA_EXPLORE: {
    id: 'ABOUT_DATA_EXPLORE',
    getFullPath: () => '/about/data-explore',
  },
  FAQ: {
    id: 'FAQ',
    getFullPath: () => '/faq',
  },
  ERROR: {
    id: 'ERROR',
    getFullPath: () => '/error',
  },
  MAINTENANCE: {
    id: 'MAINTENANCE',
    getFullPath: (message: string) => `/maintenance?message=${encodeURIComponent(message)}`,
  },
  BOTTOM_SHEET_BLANK: {
    id: 'BOTTOM_SHEET_BLANK',
    getFullPath: () => '/bottom-sheet/blank',
  },
} as const;

const TEST_PAGES: RouteObject[] = [
  {
    path: '',
    id: ROUTE_PATHS.PAGE_LIST.id,
    element: <PageListPage />,
  },
];

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    children: [
      ...(import.meta.env.VITE_PROFILE !== 'prod' ? TEST_PAGES : []),
      {
        path: '/:vin',
        children: [
          {
            path: 'home',
            id: ROUTE_PATHS.HOME.id,
            element: <VinGuard>{(vin) => <HomePage vin={vin} />}</VinGuard>,
          },
          {
            path: 'diag',
            children: [
              {
                path: '',
                id: ROUTE_PATHS.DIAG.id,
                element: <VinGuard>{(vin) => <DiagPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'tire',
                id: ROUTE_PATHS.DIAG_TIRE.id,
                element: <VinGuard>{(vin) => <DiagTirePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'cpo',
                children: [
                  {
                    path: '',
                    id: ROUTE_PATHS.CPO.id,
                    element: <VinGuard>{(vin) => <DiagCpoPage vin={vin} />}</VinGuard>,
                  },
                  {
                    path: 'recall',
                    id: ROUTE_PATHS.CPO_RECALL.id,
                    element: <VinGuard>{(vin) => <DiagCpoRecallPage vin={vin} />}</VinGuard>,
                  },
                ],
              },
              {
                path: 'battery',
                id: ROUTE_PATHS.DIAG_BATTERY.id,
                element: <VinGuard>{(vin) => <DiagBatteryPage vin={vin} />}</VinGuard>,
                children: [
                  {
                    path: 'discharge',
                    id: ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.id,
                    element: <VinGuard>{(vin) => <DiagBatteryDischargePage vin={vin} />}</VinGuard>,
                  },
                  {
                    path: 'lifespan',
                    id: ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.id,
                    element: <VinGuard>{(vin) => <DiagBatteryLifespanPage vin={vin} />}</VinGuard>,
                  },
                ],
              },
              {
                path: 'brake',
                id: ROUTE_PATHS.DIAG_BRAKE.id,
                element: <VinGuard>{(vin) => <DiagBrakePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'sest/category/:sestCategory',
                id: ROUTE_PATHS.DIAG_SEST_$CATEGORY.id,
                element: <VinGuard>{(vin) => <SestCategoryDetailPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'exchange/select',
                id: ROUTE_PATHS.DIAG_EXCHANGE_SELECT.id,
                element: <VinGuard>{(vin) => <SestExchangeSelectPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'exchange/register',
                id: ROUTE_PATHS.DIAG_EXCHANGE_REGISTER.id,
                element: <VinGuard>{(vin) => <SestExchangeRegisterPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'exchange/history',
                id: ROUTE_PATHS.DIAG_EXCHANGE_HISTORY.id,
                element: <VinGuard>{(vin) => <DiagExchangeHistoryPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'exchange/history/:historyId/edit',
                id: ROUTE_PATHS.DIAG_EXCHANGE_HISTORY_DETAIL_EDIT.id,
                element: (
                  <VinGuard>
                    {(vin) => (
                      <HistoryIdGuard vin={vin}>
                        {(vin, historyId) => (
                          <DiagExchangeHistoryDetailEditPage
                            vin={vin}
                            historyId={historyId}
                          />
                        )}
                      </HistoryIdGuard>
                    )}
                  </VinGuard>
                ),
              },
            ],
          },
          {
            path: 'mileage',
            element: <VinGuard>{(vin) => <MileagePage vin={vin} />}</VinGuard>,
            children: [
              {
                path: 'eco',
                id: ROUTE_PATHS.MILEAGE_ECO.id,
                element: <VinGuard>{(vin) => <EcoMileagePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'safe-driving',
                id: ROUTE_PATHS.MILEAGE_SAFE_DRIVING.id,
                element: <VinGuard>{(vin) => <MileageSafeDrivingPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'balance',
                id: ROUTE_PATHS.MILEAGE_BALANCE.id,
                element: <VinGuard>{(vin) => <MileageBalancePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'intro',
                id: ROUTE_PATHS.MILEAGE_INTRO.id,
                element: <VinGuard>{(vin) => <MileageIntroPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'participation-guide',
                id: ROUTE_PATHS.MILEAGE_PARTICIPATION_GUIDE.id,
                element: <VinGuard>{(vin) => <MileageParticipationGuidePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'register',
                id: ROUTE_PATHS.MILEAGE_REGISTER.id,
                element: <VinGuard>{(vin) => <MileageRegisterPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'first-register',
                id: ROUTE_PATHS.MILEAGE_FIRST_REGISTER.id,
                element: <VinGuard>{(vin) => <MileageFirstRegisterPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'mid-register',
                id: ROUTE_PATHS.MILEAGE_MID_REGISTER.id,
                element: <VinGuard>{(vin) => <MileageMidRegisterPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'agreement',
                id: ROUTE_PATHS.MILEAGE_AGREEMENT.id,
                element: <VinGuard>{(vin) => <MileageAgreementPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'agreement/:termsId',
                id: ROUTE_PATHS.MILEAGE_AGREEMENT_DETAIL.id,
                element: <VinGuard>{(vin) => <MileageAgreementDetailPage vin={vin} />}</VinGuard>,
              },
            ],
          },
          {
            path: 'ubi',
            children: [
              {
                path: '',
                id: ROUTE_PATHS.UBI.id,
                element: <VinGuard>{(vin) => <UbiPage vin={vin} />}</VinGuard>,
              },
              {
                path: ':serviceId',
                children: [
                  {
                    path: '',
                    id: ROUTE_PATHS.UBI_DETAIL_$SERVICE_ID.id,
                    element: (
                      <VinGuard>
                        {(vin) => (
                          <InsuranceServiceIdGuard>
                            {(serviceId) => (
                              <UbiDetailPage
                                vin={vin}
                                serviceId={serviceId}
                              />
                            )}
                          </InsuranceServiceIdGuard>
                        )}
                      </VinGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'history',
            children: [
              {
                path: '',
                id: ROUTE_PATHS.HISTORY.id,
                element: <VinGuard>{(vin) => <HistoryPage vin={vin} />}</VinGuard>,
              },
              {
                path: ':date',
                id: ROUTE_PATHS.HISTORY_$DATE.id,
                element: (
                  <VinGuard>
                    {(vin) => (
                      <DateGuard>
                        {(date) => (
                          <HistoryDatePage
                            vin={vin}
                            date={date}
                          />
                        )}
                      </DateGuard>
                    )}
                  </VinGuard>
                ),
              },
              {
                path: 'filtered/filter',
                id: ROUTE_PATHS.HISTORY_FILTERED_FILTER.id,
                element: <VinGuard>{(vin) => <FilteredDateHistoryFilterPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'filtered/result',
                id: ROUTE_PATHS.HISTORY_FILTERED_RESULT.id,
                element: <VinGuard>{(vin) => <FilteredDateHistoryResultPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'trip/:tripId',
                id: ROUTE_PATHS.HISTORY_$TRIP_ID.id,
                element: <VinGuard>{(vin) => <HistoryTripDetailPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'setting',
                id: ROUTE_PATHS.HISTORY_SETTING.id,
                element: <VinGuard>{(vin) => <HistorySettingPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'download',
                id: ROUTE_PATHS.HISTORY_DOWNLOAD.id,
                element: <VinGuard>{(vin) => <HistoryDownloadPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'tags',
                id: ROUTE_PATHS.HISTORY_TAGS.id,
                element: <VinGuard>{(vin) => <HistoryManageTagsPage vin={vin} />}</VinGuard>,
              },
            ],
          },
          {
            path: 'safe-driving',
            children: [
              {
                path: '',
                id: ROUTE_PATHS.SAFE_DRIVING.id,
                element: <VinGuard>{(vin) => <SafeDrivingPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'report',
                id: ROUTE_PATHS.SAFE_DRIVING_REPORT.id,
                element: <VinGuard>{(vin) => <SafeDrivingReportPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'terms',
                id: ROUTE_PATHS.SAFE_DRIVING_TERMS.id,
                element: <VinGuard>{(vin) => <SafeDrivingTermsPage vin={vin} />}</VinGuard>,
              },
            ],
          },
          {
            path: 'capital',
            children: [
              {
                path: '',
                id: ROUTE_PATHS.CAPITAL_BRIDGE.id,
                element: <VinGuard>{(vin) => <CapitalBridgePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'short',
                id: ROUTE_PATHS.CAPITAL_SHORT.id,
                element: <VinGuard>{(vin) => <CapitalShortHomePage vin={vin} />}</VinGuard>,
              },
              {
                path: 'terms',
                children: [
                  {
                    path: '',
                    id: ROUTE_PATHS.CAPITAL_TERMS.id,
                    element: <VinGuard>{(vin) => <CapitalTermsPage vin={vin} />}</VinGuard>,
                  },
                  {
                    path: '3rd',
                    id: ROUTE_PATHS.CAPITAL_TERMS_3RD_DETAIL.id,
                    element: <CapitalTerms3rdDetailPage />,
                  },
                  {
                    path: 'account-book',
                    id: ROUTE_PATHS.CAPITAL_TERMS_ACCOUNT_BOOK_DETAIL.id,
                    element: <CapitalTermsAccountBookDetailPage />,
                  },
                  {
                    path: ':termsId',
                    id: ROUTE_PATHS.CAPITAL_TERMS_DETAIL.id,
                    element: <CapitalTermsDetailPage />,
                  },
                ],
              },
              // [2025-08-21:cy.seo] FIXME: 풀-페이지 iframe 은 안쓰는 것으로 아는데 확실하면 날릴것
              {
                path: 'page',
                id: ROUTE_PATHS.CAPITAL_FULL_PAGE.id,
                element: <VinGuard>{(vin) => <CapitalFullPage vin={vin} />}</VinGuard>,
              },
              // [2025-07-24:cy.seo] FIXME: 캐피탈 이벤트 (25.09~) 종료 후 연관 코드 제거할것 EVENT2
              {
                path: 'event1',
                id: ROUTE_PATHS.CAPITAL_EVENT_1.id,
                element: <VinGuard>{(vin) => <CapitalEvent1Page vin={vin} />}</VinGuard>,
              },
            ],
          },
          {
            path: 'public-service',
            children: [
              {
                path: 'vehicle-inspection',
                children: [
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_NEW.id,
                    path: 'new',
                    element: <VinGuard>{(vin) => <VehicleInspectionNewVehiclePage vin={vin} />}</VinGuard>,
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_ADD.id,
                    path: 'add',
                    element: <VehicleInspectionAddVehiclePage />,
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_EDIT.id,
                    path: 'edit',
                    element: <VinGuard>{(vin) => <VehicleInspectionEditVehiclePage vin={vin} />}</VinGuard>,
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_ABOUT.id,
                    path: 'about',
                    element: <VinGuard>{(vin) => <VehicleInspectionAboutPage vin={vin} />}</VinGuard>,
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_NEW.id,
                    path: 'reservation/new',
                    element: <VinGuard>{(vin) => <VehicleInspectionNewReservationPage vin={vin} />}</VinGuard>,
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER.id,
                    path: 'reservation/:reservationNumber',
                    element: (
                      <VinGuard>
                        {(vin) => (
                          <ReservationNumberGuard>
                            {(reservationNumber) => (
                              <VehicleInspectionReservationPage
                                vin={vin}
                                reservationNumber={reservationNumber}
                              />
                            )}
                          </ReservationNumberGuard>
                        )}
                      </VinGuard>
                    ),
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_EDIT.id,
                    path: 'reservation/:reservationNumber/edit',
                    element: (
                      <VinGuard>
                        {(vin) => (
                          <ReservationNumberGuard>
                            {(reservationNumber) => (
                              <VehicleInspectionEditReservationPage
                                vin={vin}
                                reservationNumber={reservationNumber}
                              />
                            )}
                          </ReservationNumberGuard>
                        )}
                      </VinGuard>
                    ),
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_PAYMENT.id,
                    path: 'reservation/:reservationNumber/payment',
                    element: (
                      <VinGuard>
                        {(vin) => (
                          <ReservationNumberGuard>
                            {(reservationNumber) => (
                              <VehicleInspectionPaymentReservationPage
                                vin={vin}
                                reservationNumber={reservationNumber}
                              />
                            )}
                          </ReservationNumberGuard>
                        )}
                      </VinGuard>
                    ),
                  },
                ],
              },
              {
                path: 'consents',
                children: [
                  {
                    path: '',
                    id: ROUTE_PATHS.PUBLIC_SERVICE_CONSENTS.id,
                    element: <VinGuard>{(vin) => <PublicServiceConsentsPage vin={vin} />}</VinGuard>,
                  },
                  {
                    path: ':category',
                    id: ROUTE_PATHS.PUBLIC_SERVICE_CONSENTS_$CATEGORY.id,
                    element: (
                      <VinGuard>
                        {(vin) => (
                          <CategoryGuard>
                            {(category) => (
                              <PublicServiceConsentsDetailPage
                                vin={vin}
                                category={category}
                              />
                            )}
                          </CategoryGuard>
                        )}
                      </VinGuard>
                    ),
                  },
                ],
              },
              {
                path: 'highway-unpaid',
                children: [
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH.id,
                    path: 'search',
                    element: <VinGuard>{(vin) => <HighwayUnpaidSearchPage vin={vin} />}</VinGuard>,
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH_$LICENSE_PLATE_NUMBER.id,
                    path: 'search/:licensePlateNumber',
                    element: (
                      <VinGuard>
                        {(vin) => (
                          <LicensePlateNumberGuard>
                            {(licensePlateNumber) => (
                              <HighwayUnpaidEditSearchPage
                                vin={vin}
                                licensePlateNumber={licensePlateNumber}
                              />
                            )}
                          </LicensePlateNumberGuard>
                        )}
                      </VinGuard>
                    ),
                  },
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_ABOUT.id,
                    path: 'about',
                    element: <VinGuard>{(vin) => <HighwayUnpaidAboutPage vin={vin} />}</VinGuard>,
                  },
                ],
              },
              {
                path: 'license-renewal',
                children: [
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_LICENSE_RENEWAL_ABOUT.id,
                    path: 'about',
                    element: <VinGuard>{(vin) => <LicenseRenewalAboutPage vin={vin} />}</VinGuard>,
                  },
                ],
              },
              {
                path: 'demerit-education',
                children: [
                  {
                    id: ROUTE_PATHS.PUBLIC_SERVICE_DEMERIT_EDUCATION_ABOUT.id,
                    path: 'about',
                    element: <VinGuard>{(vin) => <DemeritEducationAboutPage vin={vin} />}</VinGuard>,
                  },
                  {
                    path: 'reservation',
                    children: [
                      {
                        path: '',
                        id: ROUTE_PATHS.PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION.id,
                        element: <VinGuard>{(vin) => <DemeritEducationReservationPage vin={vin} />}</VinGuard>,
                      },
                      {
                        id: ROUTE_PATHS.PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_EDIT.id,
                        path: 'edit',
                        element: <VinGuard>{(vin) => <DemeritEducationReservationEditPage vin={vin} />}</VinGuard>,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'payment',
        children: [
          {
            path: 'hmgpay/callback',
            id: ROUTE_PATHS.HMG_PAY_PAYMENT_CALLBACK.id,
            element: <PaymentHmgPayCallbackPage />,
          },
          {
            path: 'express-pay/callback',
            id: ROUTE_PATHS.EXPRESS_PAY_PAYMENT_CALLBACK.id,
            element: <PaymentExpressPayCallbackPage />,
          },
        ],
      },

      {
        path: 'about',
        id: ROUTE_PATHS.ABOUT.id,
        element: <AboutPage />,
      },
      {
        path: 'about/data-explore',
        id: ROUTE_PATHS.ABOUT_DATA_EXPLORE.id,
        element: <AboutDataExplorePage />,
      },
      {
        path: 'faq',
        id: ROUTE_PATHS.FAQ.id,
        element: <FaqPage />,
      },
      {
        path: 'bottom-sheet',
        element: <BottomSheetLayout />,
        children: [
          {
            id: ROUTE_PATHS.BOTTOM_SHEET_CAPITAL_EVENT_1_INTRO.id,
            path: 'capital/event1',
            element: <CapitalEvent1BottomSheetPage />,
          },
          {
            id: ROUTE_PATHS.BOTTOM_SHEET_BLANK.id,
            path: 'blank',
            element: <BlankBottomSheetPage />,
          },
          {
            id: ROUTE_PATHS.BOTTOM_SHEET_INTRODUCE.id,
            path: 'introduce',
            element: <IntroduceBottomSheetPage />,
          },
          {
            path: 'mileage',
            children: [
              {
                path: 'signup-eligibility',
                id: ROUTE_PATHS.BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY.id,
                element: <MileageSignupEligibilityBottomSheetPage />,
              },
              // /**
              //  * [2026-02-09:Jeyoung Lee] FIXME: 서울시 임시 페이지
              //  * @link https://hmg.atlassian.net/browse/DRIVINGINS-2884
              //  */
              {
                path: 'signup-eligibility-imsi',
                id: ROUTE_PATHS.BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY_IMSI.id,
                element: <MileageSignupEligibilityImsiBottomSheetPage />,
              },
            ],
          },
          {
            path: 'diag',
            children: [
              {
                path: 'update-odometer',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_UPDATE_ODOMETER.id,
                element: <UpdateOdometerBottomSheetPage />,
              },
              {
                path: 'tire-warning',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_TIRE_WARNING.id,
                element: <TireWarningBottomSheetPage />,
              },
              {
                path: ':vin/tire-selection',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_TIRE_SELECTION.id,
                element: <VinGuard>{(vin) => <TireSelectionBottomSheetPage vin={vin} />}</VinGuard>,
              },
              {
                path: 'sest/management-setting/:sestCode',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING.id,
                element: <SestManagementSettingBottomSheetPage />,
              },
              {
                path: 'ev-battery/emergency-call',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_EMERGENCY_CALL.id,
                element: <BatteryEmergencyCallBottomSheetPage />,
              },
              {
                path: 'ev-battery/charging-habit',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_CHARGING_HABIT.id,
                element: <EvBatteryChargingHabitBottomSheetPage />,
              },
              {
                path: 'ev-battery/performance',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE.id,
                element: <EvBatteryPerformaceBottomSheetPage />,
              },
              {
                path: 'ev-battery/coach',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_COACH.id,
                element: <EvBatteryCoachBottomSheetPage />,
              },
              {
                path: 'exchange/history/auto',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_AUTO.id,
                element: <SestExchangeHistoryAutoBottomSheetPage />,
              },
              {
                path: 'exchange/history/manual',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_MANUAL.id,
                element: <SestExchangeHistoryManualBottomSheetPage />,
              },
              {
                path: 'exchange/register/date-picker',
                id: ROUTE_PATHS.BOTTOM_SHEET_DIAG_EXCHANGE_REGISTER_DATE_PICKER.id,
                element: <DiagExchangeRegisterDatePickerBottomSheetPage />,
              },
            ],
          },
          {
            path: 'history',
            children: [
              {
                path: 'select-month',
                id: ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.id,
                element: <SelectMonthPage />,
              },
              {
                path: 'download/datepicker',
                id: ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.id,
                element: <DatePickerPage />,
              },
              {
                path: 'filter-select',
                id: ROUTE_PATHS.BOTTOM_SHEET_HISTORY_FILTER_SELECT.id,
                element: <HistoryFilterSelectPage />,
              },
              {
                path: 'driver-select',
                id: ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DRIVER_SELECT.id,
                element: <HistoryDriverSelectPage />,
              },
              {
                path: 'tag-select',
                id: ROUTE_PATHS.BOTTOM_SHEET_HISTORY_TAG_SELECT.id,
                element: <HistoryTagSelectPage />,
              },
              {
                path: 'tags/form',
                id: ROUTE_PATHS.BOTTOM_SHEET_HISTORY_TAGS_FORM.id,
                element: <HistoryTagFormBottomSheetPage />,
              },
            ],
          },
          {
            path: 'safe-driving',
            children: [
              {
                path: 'navigation-update',
                id: ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_NAVIGATION_UPDATE.id,
                element: <SafeDrivingNavigationUpdateBottomSheetPage />,
              },
              {
                path: 'agree',
                id: ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_AGREE.id,
                element: <SafeDrivingAgreeBottomSheetPage />,
              },
              {
                path: 'withdraw',
                id: ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_WITHDRAW.id,
                element: <SafeDrivingWithdrawBottomSheetPage />,
              },
              {
                path: 'guide',
                id: ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_GUIDE.id,
                element: <SafeDrivingGuideBottomSheetPage />,
              },
            ],
          },
          {
            path: 'ubi',
            children: [
              {
                path: 'no-insurance',
                id: ROUTE_PATHS.BOTTOM_SHEET_UBI_NO_INSURANCE.id,
                element: <UbiNoInsurancePage />,
              },
              {
                path: 'agree',
                id: ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_AGREE.id,
                element: <UbiAgreeBottomSheetPage />,
              },
              {
                path: 'withdraw',
                id: ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_WITHDRAW.id,
                element: <UbiWithdrawBottomPage />,
              },
            ],
          },
          {
            path: 'capital',
            children: [
              {
                path: 'withdraw',
                id: ROUTE_PATHS.BOTTOM_SHEET_CAPITAL_WITHDRAW.id,
                element: <CapitalWithdrawBottomSheetPage />,
              },
            ],
          },
          {
            path: 'vehicle',
            children: [
              {
                path: 'delete',
                id: ROUTE_PATHS.BOTTOM_SHEET_VEHICLE_DELETE.id,
                element: <VehicleDetailBottomSheetPage />,
              },
            ],
          },
          {
            path: 'public-service',
            children: [
              {
                path: 'withdraw',
                id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVCIE_WITHDRAW.id,
                element: <PublicServiceWithdrawBottomSheetPage />,
              },
              {
                path: 'highway-unpaid',
                children: [
                  {
                    path: 'edit/confirm',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_HIGHWAY_UNPAID_EDIT_CONFIRM.id,
                    element: <HighwayUnpaidEditConfirmBottomSheetPage />,
                  },
                ],
              },
              {
                path: 'license-renewal',
                children: [
                  {
                    path: 'fail-customer-info',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_CUSTOMER_INFO.id,
                    element: <LicenseRenewalFailCustomerInfoBottomSheetPage />,
                  },
                  {
                    path: 'fail-system-error',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_SYSTEM_ERROR.id,
                    element: <LicenseRenewalFailSystemBottomSheetPage />,
                  },
                  {
                    path: 'fail-police-case',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_POLICE_CASE.id,
                    element: <LicenseRenewalFailPoliceCaseBottomSheetPage />,
                  },
                  {
                    path: 'fail-over-75years',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_LICENSE_RENEWAL_FAIL_OVER_75YEARS.id,
                    element: <LicenseRenewalFailOver75YearsBottomSheetPage />,
                  },
                ],
              },
              {
                path: 'demerit-education',
                children: [
                  {
                    path: 'reservation-fail',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_FAIL.id,
                    element: <DemeritEducationReservationFailBottomSheetPage />,
                  },
                  {
                    path: 'reservation-success',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_SUCCESS.id,
                    element: <DemeritEducationReservationSuccessBottomSheetPage />,
                  },
                  {
                    path: 'fail-system-error',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_SYSYEM_ERROR.id,
                    element: <DemeritEducationFailSystemErrorBottomSheetPage />,
                  },
                  {
                    path: 'fail-customer-info',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_FAIL_CUSTOMER_INFO.id,
                    element: <DemeritEducationFailCustomerInfoBottomSheetPage />,
                  },
                ],
              },
              {
                path: 'vehicle-inspection',
                children: [
                  {
                    path: 'owner-authentication-failure',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_OWNER_AUTHENTICATION_FAILURE.id,
                    element: <OwnerAuthenticationFailureBottomSheetPage />,
                  },
                  {
                    path: 'general-failure',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_GENERAL_FAILURE.id,
                    element: <GeneralFailureBottomSheetPage />,
                  },
                  {
                    path: 'authentication-count-exceeded',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_AUTHENTICATION_COUNT_EXCEEDED.id,
                    element: <AuthenticationCountExceededBottomSheetPage />,
                  },
                  {
                    path: 'reservation-confirm',
                    id: ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_CONFIRM.id,
                    element: <VehicleInspectionReservationConfirmBottomSheetPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'guide',
        children: [
          {
            path: 'cpo',
            id: ROUTE_PATHS.CPO_GUIDE.id,
            element: <CpoGuidePage />,
          },
          {
            path: 'mileage',
            id: ROUTE_PATHS.GUIDE_MILEAGE.id,
            element: <MileageGuidePage />,
          },
          {
            path: 'safe-driving',
            id: ROUTE_PATHS.SAFE_DRIVING_GUIDE.id,
            element: <SafeDrivingGuidePage />,
          },
        ],
      },
      {
        path: 'error',
        id: ROUTE_PATHS.ERROR.id,
        element: <ErrorPage />,
      },
      {
        path: 'maintenance',
        id: ROUTE_PATHS.MAINTENANCE.id,
        element: <MaintenancePage />,
      },
    ],
  },
];
